import { Receipt } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../components/ui/card";
import { DatePicker } from "../../../../../components/ui/date-picker";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { useCreateCreditoAdhoc, useFranquiasAdHoc } from "../hooks/use-credito-adhoc";
import { Franquia } from "../types";

interface CreateFormProps {
  dbName: string;
  onSuccess: () => void;
}

const TAXA = 0.003;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value / 100
  );

export function CreateForm({ dbName, onSuccess }: CreateFormProps) {
  const { data: franquias = [], isLoading: loadingFranquias } = useFranquiasAdHoc(dbName);
  const createMutation = useCreateCreditoAdhoc();

  const [selectedOps, setSelectedOps] = useState("");
  const [dataVencimento, setDataVencimento] = useState<Date | undefined>(undefined);
  const [valorCompraInput, setValorCompraInput] = useState("");

  const valorCompra = Math.round(parseFloat(valorCompraInput.replace(",", ".")) * 100) || 0;
  const valorFace = valorCompra > 0 ? Math.round(valorCompra / (1 - TAXA)) : 0;
  const valorDesagio = valorFace - valorCompra;

  const canSubmit =
    !!selectedOps &&
    !!dataVencimento &&
    valorCompra > 0 &&
    !createMutation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || !dataVencimento) return;
    const dataVencimentoStr = dataVencimento.toISOString().split("T")[0];
    await createMutation.mutateAsync(
      { dbName, ops: selectedOps, dataVencimento: dataVencimentoStr, valorCompra },
      {
        onSuccess: () => {
          setSelectedOps("");
          setDataVencimento(undefined);
          setValorCompraInput("");
          onSuccess();
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Receipt className="h-4 w-4" />
          Novo Crédito AdHoc
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Franchise select */}
          <div className="space-y-1">
            <Label>Franquia</Label>
            <Select
              value={selectedOps}
              onValueChange={setSelectedOps}
              disabled={loadingFranquias || !dbName}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    loadingFranquias ? "Carregando..." : "Selecione a franquia"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {franquias.map((f: Franquia) => (
                  <SelectItem key={f.ops} value={f.ops}>
                    {f.razaoSocial || (f as any).razao_social} ({f.ops})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Data Vencimento */}
          <div className="space-y-1">
            <Label>Data de Vencimento</Label>
            <DatePicker date={dataVencimento} setDate={setDataVencimento} />
          </div>

          {/* Valor Compra */}
          <div className="space-y-1">
            <Label>Valor de Compra (R$)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={valorCompraInput}
              onChange={(e) => setValorCompraInput(e.target.value)}
            />
          </div>
        </div>

        {/* Read-only calculated fields */}
        {valorCompra > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-4 rounded-md bg-muted/40 p-3">
            <div>
              <p className="text-xs text-muted-foreground">Taxa</p>
              <p className="font-medium">0,30%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Valor Face</p>
              <p className="font-semibold text-primary">{formatCurrency(valorFace)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Deságio</p>
              <p className="font-medium text-yellow-600">{formatCurrency(valorDesagio)}</p>
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createMutation.isPending ? "Processando..." : "Cadastrar Crédito"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
